// FIQ-1658: shared helpers for the per-task detail behind each data-quality warning.
// Maps alert ids to the costResult.dataQualityDetails keys produced by the cost engine
// and builds the plain-English per-task reasons used by both the alert UI and the
// Excel export. Older saved cost results have no dataQualityDetails block, so every
// lookup here must tolerate its absence.
import { formatMoney } from "../format-utils"

const DATA_QUALITY_DETAIL_CONFIG = {
    "zero-duration": { key: "zeroDuration", category: "Zero Duration" },
    "no-frequency": { key: "noFrequency", category: "No Frequency" },
    "unassigned-skills": { key: "unassignedSkill", category: "Unassigned Skill" },
    "unmapped-skills": { key: "unmappedSkill", category: "Unmapped Skill" },
    "unpriced-trades": { key: "unpricedTrade", category: "Unpriced Trade" },
    "phantom-cost": { key: "phantomCost", category: "Phantom Cost" },
}

export function getDataQualityCategoryLabel(alertId) {
    return DATA_QUALITY_DETAIL_CONFIG[alertId]?.category || "Data Quality"
}

export function getDataQualityDetail(costResult, alertId) {
    const config = DATA_QUALITY_DETAIL_CONFIG[alertId]
    if (!config) return null
    const detail = costResult?.dataQualityDetails?.[config.key]
    if (!detail?.tasks?.length) return null
    return detail
}

export function buildFlaggedTaskReason(alertId, task, currencySymbol) {
    switch (alertId) {
        case "zero-duration":
            return "No SFG20 standard duration, so it contributes no labour cost."
        case "no-frequency":
            return "No valid frequency, so it is excluded from costing."
        case "unassigned-skills":
            return `No skill assigned, so it is excluded from costing${hoursSuffix(task)}.`
        case "unmapped-skills":
            return `Skill "${task.skillName || "unknown"}" is not mapped to a trade, so it is excluded from costing${hoursSuffix(task)}.`
        case "unpriced-trades":
            return `Trade "${task.tradeName || "unknown"}" has no rate on the rate card, so it is excluded from costing${hoursSuffix(task)}.`
        case "phantom-cost":
            return `Zero duration but billed ${currencySymbol}${formatMoney(task.cost || 0)}/yr through the trade's minimum billing period.`
        default:
            return ""
    }
}

// The regime group id embedded in a scenario's regimeId (e.g. "live|abc:1" -> "abc"),
// used to deep-link flagged tasks back to the regime on the schedules page. Strips all
// regime version prefixes (matching REGIME_VERSION_PREFIXES used server-side).
export function getRegimeGroupId(regimeId) {
    return regimeId?.replace(/^(live|ready|publish)\|/, "")?.split(":")?.[0] || null
}

export function buildDataQualityDetailExportRows({ costResult, currencySymbol }) {
    const rows = []
    for (const [alertId, config] of Object.entries(DATA_QUALITY_DETAIL_CONFIG)) {
        const detail = getDataQualityDetail(costResult, alertId)
        if (!detail) continue
        for (const task of detail.tasks) {
            rows.push(toExportRow(alertId, config, task, currencySymbol))
        }
        // The engine caps the stored rows; say so rather than silently truncating the sheet.
        if (detail.total > detail.tasks.length) {
            rows.push(toTruncationRow(config, detail))
        }
    }
    return rows
}

// FIQ-1658: single-warning export rows for the drawer's "export affected tasks" action.
// No Category column (the sheet is one warning). When `total` exceeds the rows supplied
// (a warning bigger than even the server-side export cap), an explicit "plus N more" row
// is appended rather than truncating silently.
export function buildFlaggedTaskExportRows(alertId, tasks, currencySymbol, total) {
    const rows = (tasks || []).map(toTaskColumns)
    if (rows.length && total > rows.length) {
        rows.push({
            ...toFlaggedTaskColumns(alertId, {}, currencySymbol),
            Task: `plus ${total - rows.length} more task(s) not included in this export`,
            Assets: "",
            Reason: "",
        })
    }
    return rows

    function toTaskColumns(task) {
        return toFlaggedTaskColumns(alertId, task, currencySymbol)
    }
}

function toExportRow(alertId, config, task, currencySymbol) {
    return {
        Category: config.category,
        ...toFlaggedTaskColumns(alertId, task, currencySymbol),
    }
}

function toFlaggedTaskColumns(alertId, task, currencySymbol) {
    return {
        Task: task.title || "",
        "Schedule Code": task.scheduleCode || "",
        Schedule: task.scheduleTitle || "",
        Skill: task.skillName || "",
        Trade: task.tradeName || "",
        Assets: task.assetCount || 0,
        "Annual Hours": task.hours || "",
        "Cost Impact": task.cost ? `${currencySymbol}${formatMoney(task.cost)}` : "",
        Reason: buildFlaggedTaskReason(alertId, task, currencySymbol),
    }
}

function toTruncationRow(config, detail) {
    return {
        Category: config.category,
        Task: `plus ${detail.total - detail.tasks.length} more task(s) not included in this export`,
        "Schedule Code": "",
        Schedule: "",
        Skill: "",
        Trade: "",
        Assets: "",
        "Annual Hours": "",
        "Cost Impact": "",
        Reason: "",
    }
}

function hoursSuffix(task) {
    if (!task.hours) return ""
    return ` (~${task.hours} hrs/yr)`
}
